/**
 * exporter.js — turning the composition into a file.
 *
 * Three lanes, chosen by what the browser can actually do, in the same shape
 * /split-screen and /video-merge already proved:
 *
 *   fast  — WebCodecs video + WebCodecs audio, muxed in one pass. Chrome, Edge,
 *           Safari 26+. Hardware encode; a 30 s 1080p clone runs in seconds.
 *   split — WebCodecs video, but the browser has no AAC encoder (Firefox). The
 *           premixed audio goes to ONE cheap FFmpeg pass that copies the video
 *           and encodes only the audio. No WASM video encoding, so no 18-minute
 *           waits.
 *   mr    — no WebCodecs at all: MediaRecorder over a live canvas. Real-time by
 *           construction, so it is the safety net and is reported as such.
 *
 * The frame content is IDENTICAL in all three: every lane calls the one
 * renderer in scene.js. Lanes differ in how bytes leave, never in what is drawn.
 *
 * Exposes window.CloneExport.
 */
(function () {
'use strict';
if (window.CloneExport) return;

var SR = 48000;

function ae(type, data) { try { window.__ae && window.__ae(type, data); } catch (_) {} }
function throwIfAborted(signal) {
    if (signal && signal.aborted) throw new DOMException('Aborted', 'AbortError');
}

var CRF = { high: 20, medium: 25, low: 30 };
function audioBitrate(q) { return q === 'high' ? '192k' : q === 'low' ? '96k' : '128k'; }

/* ───────────────────── per-frame mattes during export ─────────────────────
   A clip on a silhouette mask needs one matte per frame. The clean-plate
   engine is arithmetic on a 320-px frame and keeps up; the AI engine is a
   neural net per frame and is why that mode warns about its cost up front. */

async function makeMatteRig(project, signal, onNote) {
    var rig = { plates: {}, ai: {}, needed: [] };
    for (var i = 1; i < project.clips.length; i++) {
        var c = project.clips[i];
        if (!c || !c.visible || !c.mask) continue;
        if (c.mask.type === 'auto') {
            var plate = c.__plate;
            if (!plate) {
                onNote && onNote('plate', i);
                plate = await window.CloneMatte.buildPlate(c, null, signal);
                c.__plate = plate;
            }
            if (plate) { rig.plates[i] = new window.CloneMatte.PlateMatte(plate); rig.needed.push(i); }
        } else if (c.mask.type === 'ai') {
            if (!window.CloneMatte.AiMatte.available()) continue;
            var eng = new window.CloneMatte.AiMatte();
            onNote && onNote('model', i);
            await eng.ready(signal);
            eng.reset();
            rig.ai[i] = eng;
            rig.needed.push(i);
        }
    }
    return rig;
}

async function fillMattes(rig, project, getFrame, out) {
    for (var k = 0; k < rig.needed.length; k++) {
        var i = rig.needed[k];
        var frame = getFrame(i);
        if (!frame) { out[i] = null; continue; }
        if (rig.plates[i]) {
            out[i] = rig.plates[i].compute(frame);
        } else if (rig.ai[i]) {
            var c = project.clips[i];
            out[i] = await rig.ai[i].compute(frame, c.vw, c.vh, true);
        }
    }
}

/* ─────────────────────────────── main ─────────────────────────────── */

/**
 * @param project
 * @param opts   { format:'mp4'|'webm', quality, W, H, fps }
 * @param hooks  { signal, onProgress(frac), onStage(key), onNote(kind, i) }
 * @returns { blob, lane, mode, duration }
 */
async function run(project, opts, hooks) {
    hooks = hooks || {};
    var signal = hooks.signal;
    var setProgress = hooks.onProgress || function () {};
    var stage = hooks.onStage || function () {};

    var W = opts.W, H = opts.H, fps = opts.fps || 30;
    var format = opts.format === 'webm' ? 'webm' : 'mp4';
    var quality = opts.quality || 'high';
    var duration = window.CloneScene.projectDuration(project);
    if (!(duration > 0)) throw new Error('empty-project');

    stage('audio');
    setProgress(0.02);
    var audioBuffer = null;
    try {
        audioBuffer = await window.CloneAudio.buildMix(project, signal, function (f) {
            setProgress(0.02 + f * 0.10);
        });
    } catch (e) {
        if (e && e.name === 'AbortError') throw e;
        ae('feature_use', { slug: 'clone-effect', f: 'audio_mix_failed', err: ((e && e.message) || '').slice(0, 60) });
    }
    throwIfAborted(signal);

    stage('mattes');
    var rig = await makeMatteRig(project, signal, hooks.onNote);
    throwIfAborted(signal);
    setProgress(0.16);

    stage('frames');
    var sources = await window.CloneSources.makeExportSources(project, signal);
    throwIfAborted(signal);

    var mattes = {};
    var drawFrame = async function (ctx, frameIndex, timeSec) {
        await sources.prepare(timeSec);
        if (rig.needed.length) await fillMattes(rig, project, sources.getFrame, mattes);
        window.CloneScene.renderFramed(ctx, project, timeSec, sources.getFrame, 1, { mattes: mattes });
    };

    var probe = { ok: false, reason: 'no-canvas-export' };
    if (window.CanvasVideoExport) {
        try {
            probe = await window.CanvasVideoExport.isSupported({
                width: W, height: H, fps: fps, format: format,
                audioSampleRate: SR, audioChannels: 2
            });
        } catch (_) { probe = { ok: false, reason: 'probe-error' }; }
    }

    var lane = probe.ok ? 'fast' : (probe.reason === 'no-audio-encoder' ? 'split' : 'mr');
    ae('feature_use', {
        slug: 'clone-effect', f: 'export_lane', lane: lane, mode: sources.mode,
        n: project.clips.length, fmt: format, dur: Math.round(duration * 10) / 10,
        w: W, h: H, hw: !!probe.hardware,
        mask: project.clips.slice(1).map(function (c) { return c.mask ? c.mask.type : '-'; }).join(','),
        music: project.music ? 1 : 0
    });

    try {
        if (lane === 'fast' || lane === 'split') {
            var blob = await window.CanvasVideoExport.encode({
                width: W, height: H, fps: fps, durationSec: duration,
                format: format, qualityCrf: CRF[quality] || 23,
                audioBuffer: lane === 'fast' ? audioBuffer : null,
                drawFrame: drawFrame, signal: signal,
                onProgress: function (f) { setProgress(0.18 + f * (lane === 'split' ? 0.62 : 0.80)); }
            });
            if (lane === 'split' && audioBuffer) {
                stage('mux');
                blob = await muxAudio(blob, audioBuffer, format, quality, signal, function (f) {
                    setProgress(0.80 + f * 0.19);
                });
            }
            setProgress(1);
            return { blob: blob, lane: lane, mode: sources.mode, duration: duration };
        }

        var mrBlob = await recorderLane(project, W, H, fps, duration, format, audioBuffer, drawFrame, signal, function (f) {
            setProgress(0.18 + f * 0.81);
        });
        setProgress(1);
        return { blob: mrBlob, lane: 'mr', mode: sources.mode, duration: duration };
    } finally {
        try { await sources.close(); } catch (_) {}
        window.CloneScene.releasePool();
    }
}

/* ─────────────────────── split lane: audio mux only ─────────────────────── */

async function muxAudio(videoBlob, audioBuffer, format, quality, signal, setProgress) {
    if (!audioBuffer || !window.FFmpegProcessor) return videoBlob;
    setProgress(0.05);
    await window.FFmpegProcessor.init(function (p) { setProgress(0.05 + p * 0.35); });
    throwIfAborted(signal);

    var ext = format === 'webm' ? 'webm' : 'mp4';
    var vName = 'ce_v.' + ext, aName = 'ce_a.wav', outName = 'ce_out.' + ext;
    await window.FFmpegProcessor._fs.write(vName, new Uint8Array(await videoBlob.arrayBuffer()));
    await window.FFmpegProcessor._fs.write(aName, window.CloneAudio.toWavBytes(audioBuffer));
    setProgress(0.55);

    var br = audioBitrate(quality);
    var acodec = ext === 'webm'
        ? ['-c:a', 'libopus', '-b:a', br]
        : ['-c:a', 'aac', '-b:a', br, '-movflags', '+faststart'];
    await window.FFmpegProcessor._fs.exec(
        ['-i', vName, '-i', aName, '-map', '0:v:0', '-map', '1:a:0', '-c:v', 'copy'].concat(acodec, ['-shortest', outName])
    );
    var bytes = await window.FFmpegProcessor._fs.read(outName);
    try {
        await window.FFmpegProcessor._fs.del(vName);
        await window.FFmpegProcessor._fs.del(aName);
        await window.FFmpegProcessor._fs.del(outName);
    } catch (_) {}
    setProgress(1);
    return new Blob([bytes], { type: ext === 'webm' ? 'video/webm' : 'video/mp4' });
}

/* ───────────────────────── MediaRecorder lane ─────────────────────────
   Real-time by construction: MediaRecorder timestamps by wall clock, so frames
   cannot be pushed faster than they play. The loop therefore paces itself to
   the clock and draws whatever the current time is, rather than marching
   through frame indices — dropping a frame is correct here, stretching the
   timeline is not.

   This lane also has to stamp the free-tier mark itself: CanvasVideoExport does
   it inside encode(), and this never goes through encode(). */

async function recorderLane(project, W, H, fps, duration, format, audioBuffer, drawFrame, signal, setProgress) {
    var cv = document.createElement('canvas');
    cv.width = W; cv.height = H;
    var ctx = cv.getContext('2d');

    var wmOn = false;
    try {
        wmOn = !!window.__wmShould && !!window.__wmDraw && window.__wmShould(window.__WM_SCOPE);
    } catch (_) { wmOn = false; }
    ae('feature_use', { slug: 'clone-effect', f: 'wm_lane', lane: 'mr', marked: wmOn ? 1 : 0, scope: window.__WM_SCOPE ? 1 : 0 });

    // The first video recorder of a Chromium tab drops its first ~2 s of
    // frames while the sound runs on. Warm the encoder at this size first.
    if (window.__mediaRecorderWarm) await window.__mediaRecorderWarm({ width: W, height: H, mimeType: pickMime(format, !!audioBuffer), videoBitsPerSecond: bitrateFor(W, H, fps) });
    if (signal && signal.aborted) throw new DOMException('aborted', 'AbortError');

    var stream = cv.captureStream(fps);
    var actx = null;
    if (audioBuffer) {
        try {
            actx = new (window.AudioContext || window.webkitAudioContext)();
            var dest = actx.createMediaStreamDestination();
            var src = actx.createBufferSource();
            src.buffer = audioBuffer;
            src.connect(dest);
            dest.stream.getAudioTracks().forEach(function (tr) { stream.addTrack(tr); });
            src.start();
        } catch (_) { actx = null; }
    }

    var mime = pickMime(format, stream.getAudioTracks().length > 0);
    if (!mime) throw new Error('no-recorder-mime');

    var rec = new MediaRecorder(stream, { mimeType: mime, videoBitsPerSecond: bitrateFor(W, H, fps) });
    var chunks = [];
    rec.ondataavailable = function (e) { if (e.data && e.data.size) chunks.push(e.data); };

    var stopped = new Promise(function (resolve) { rec.onstop = resolve; });
    rec.start(1000);

    var t0 = performance.now();
    var aborted = false;
    try {
        while (true) {
            if (signal && signal.aborted) { aborted = true; break; }
            var t = (performance.now() - t0) / 1000;
            if (t >= duration) break;
            await drawFrame(ctx, Math.round(t * fps), t);
            if (wmOn) { try { window.__wmDraw(ctx, W, H, window.__WM_TEXT); } catch (_) {} }
            setProgress(Math.min(0.99, t / duration));
            await new Promise(function (r) { requestAnimationFrame(function () { r(); }); });
        }
    } finally {
        try { rec.stop(); } catch (_) {}
        await stopped;
        if (actx) { try { actx.close(); } catch (_) {} }
        stream.getTracks().forEach(function (tr) { try { tr.stop(); } catch (_) {} });
    }
    if (aborted) throw new DOMException('Aborted', 'AbortError');
    return new Blob(chunks, { type: mime.split(';')[0] });
}

function pickMime(format, withAudio) {
    var want = format === 'webm'
        ? ['video/webm;codecs=vp9,opus', 'video/webm;codecs=vp8,opus', 'video/webm']
        : ['video/mp4;codecs=avc1.42E01E,mp4a.40.2', 'video/mp4', 'video/webm;codecs=vp9,opus', 'video/webm'];
    for (var i = 0; i < want.length; i++) {
        var m = want[i];
        // A video-only recording must not ask for an audio codec: Firefox
        // rejects the whole mimeType rather than ignoring the extra codec, and
        // the export dies at start() with a message about audio tracks.
        if (!withAudio) m = m.replace(/,\s*(opus|mp4a\.40\.2)/, '');
        if (window.MediaRecorder && MediaRecorder.isTypeSupported(m)) return m;
    }
    return null;
}

function bitrateFor(w, h, fps) {
    var px = w * h;
    var base = px > 2000000 ? 12e6 : px > 900000 ? 8e6 : 4e6;
    return Math.round(base * (fps > 30 ? 1.4 : 1));
}

window.CloneExport = { run: run, CRF: CRF };
})();
